import qs, { stringify } from 'qs';

import { CookieUtil } from '../utils/cookie-util';

import { ApiError } from './api-error';
import * as apiFetchRequestModule from './api-fetch-request';
import * as apiSuperAgentRequestModule from './api-super-agent-request';
import { BadRequestError } from './bad-request-error';
import { ForbiddenError } from './forbidden-error';
import { NotFoundError } from './not-found-error';
import { UnauthorizedError } from './unauthorized-error';
import type { RequestProps, ResponseProps } from './api.types';
import { UnprocessableEntityError } from './unprocessable-entity-error';
import { InternalServerError } from './internal-server-error';
import { BadGatewayError } from './bad-gateway-error';
import { GoneError } from './gone-error';
import { MethodNotAllowedError } from './method-not-allowed-error';

// Re-exported as namespaces so tests can spy on the exact reference used by the dispatcher.

export interface ImplProps extends RequestProps {
  fullDomain: string;
}

export interface ImplResponse {
  ok: boolean;
  status?: number;
  body: unknown;
  text: string;
  response: unknown;
}

/**
 * For the Java API, objects need to be an encoded JSON string. Here, we extract and
 * encode each object and append it to the existing query string
 * @param {*} query The query string containing the potential object(s) to be
 * encoded
 */
const prepareQuery = (
  query?: Record<string, unknown>,
  encodeQuery?: boolean
) => {
  if (!query) {
    return '';
  }

  if (!encodeQuery) {
    return `?${qs.stringify(query)}`;
  }
  const serializedObjects = [];
  const updatedQuery: Record<string, unknown> = {};

  for (const property in query) {
    if (query[property] && typeof query[property] === 'object') {
      serializedObjects.push(
        [property, encodeURIComponent(JSON.stringify(query[property]))].join(
          '='
        )
      );
    } else {
      updatedQuery[property] = query[property];
    }
  }

  return `?${[stringify(updatedQuery), ...serializedObjects].join('&')}`;
};

const createErrorFromResponse = (responseProps: ResponseProps): Error => {
  switch (responseProps.status) {
    case 400: {
      return new BadRequestError(responseProps);
    }
    case 403: {
      return new ForbiddenError(responseProps);
    }
    case 401: {
      return new UnauthorizedError(responseProps);
    }
    case 404: {
      return new NotFoundError(responseProps);
    }
    case 405: {
      return new MethodNotAllowedError(responseProps);
    }
    case 410: {
      return new GoneError(responseProps);
    }
    case 422: {
      return new UnprocessableEntityError(responseProps);
    }
    case 500: {
      return new InternalServerError(responseProps);
    }
    case 502: {
      return new BadGatewayError(responseProps);
    }
    default: {
      return new ApiError(responseProps);
    }
  }
};

export async function apiRequest<ResponseType>(
  props: RequestProps
): Promise<ResponseType> {
  if (props.cookieTokenRefresher) {
    await props.cookieTokenRefresher.resetTimeout();
  }

  const fullDomain = `${props.domain}${props.endpoint}${prepareQuery(
    props.query,
    props.encodeQuery ?? true
  )}`;
  const implProps: ImplProps = { ...props, fullDomain };

  const result = props.useFetch
    ? await apiFetchRequestModule.apiFetchRequest(implProps)
    : await apiSuperAgentRequestModule.apiSuperAgentRequest(implProps);

  if (!result.ok) {
    if (
      result.status === 401 &&
      typeof window !== 'undefined' &&
      props.cookieDomain
    ) {
      CookieUtil.clearUserAndReload(props.cookieDomain);
    }
    throw createErrorFromResponse({
      endpoint: props.endpoint,
      method: props.method,
      status: result.status,
      body: (result.body as ResponseProps['body']) || {},
      headers: props.headers,
      response: result.response,
    });
  }

  if (
    result.body &&
    typeof result.body === 'object' &&
    'redirect' in result.body &&
    typeof (result.body as { redirect?: unknown }).redirect === 'string'
  ) {
    const redirectEndpoint = (result.body as { redirect: string }).redirect;
    const rebody = await apiRequest<ResponseType>({
      endpoint: redirectEndpoint,
      method: props.method,
      body: props.body,
      headers: props.headers,
    } as RequestProps);
    return {
      ...rebody,
      redirected: redirectEndpoint,
    };
  }

  // For text/HTML responses, body is undefined and the raw text is returned instead
  const bodyIsEmpty = result.body === undefined;
  const textIsString = result.text.length > 0;

  return (
    bodyIsEmpty && textIsString ? result.text : result.body
  ) as ResponseType;
}

export * as apiFetchRequestModule from './api-fetch-request';
export * as apiSuperAgentRequestModule from './api-super-agent-request';
